import { Box, Modal, Pagination, useMediaQuery } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react'
import { RiDeleteBin6Fill, RiEdit2Fill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from "formik";
import * as Yup from "yup";
import { createBlog, deleteBlog, getAllBlogs, updateBlog } from '../Redux/Slice/blog.slice';
import { FaEye, FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

export default function Blog() {
    const [blogData, setBlogData] = useState("");
    const [delOpen, setDelOpen] = useState(false);
    const dispatch = useDispatch();
    const [createopen, setCreateopen] = useState(false);
    const blog = useSelector(state => state.blog.blogs);
    const loading = useSelector(state => state.blog.loading);
    const isSmallScreen = useMediaQuery("(max-width:425px)");
    const fileInputRef = useRef(null);
    const [searchValue, setSearchValue] = useState('');
    const [viewOpen, setViewOpen] = useState(false);
    const [selectedBlog, setSelectedBlog] = useState(null);

    const validationSchema = Yup.object({
        title: Yup.string().required("Blog name is required"),
        content: Yup.string().required("Blog content is required"),
        author: Yup.string().required("Blog author is required"),
        blog_images: Yup.array()
            .of(
                Yup.mixed().test(
                    "fileFormat",
                    "Unsupported Format",
                    (value) => {
                        if (!value) return true;
                        if (typeof value === "string") return true;
                        return [
                            "image/jpeg", "image/png", "image/gif", "image/webp", "image/jpg", "image/bmp", "image/svg+xml", "image/tiff", "image/x-icon",
                        ].includes(value.type);
                    }
                )
            )
            .min(1, "At least one blog image is required"),
    });

    const formik = useFormik({
        initialValues: {
            title: "",
            content: "",
            blog_images: [],
            author: ""
        },
        validationSchema,
        onSubmit: async (values) => {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("content", values.content);
            formData.append("author", values.author);

            if (values.blog_images) {
                values.blog_images.forEach((img) => {
                    formData.append("blog_images", img);
                });
            }
            if (blogData) {
                dispatch(updateBlog({ _id: blogData._id, formData }));
            } else {
                dispatch(createBlog(formData));
            }
            handleCreateClose();
        },
    });

    useEffect(() => {
        dispatch(getAllBlogs());
    }, [dispatch]);

    const filteredData = Array.isArray(blog)
        ? blog.filter(data =>
            data?.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
            data?.content?.toLowerCase().includes(searchValue.toLowerCase()) ||
            data?.author?.toLowerCase().includes(searchValue.toLowerCase())
        )
        : [];

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleOpen = (data) => {
        setCreateopen(true);
        setBlogData(data);
        if (data) {
            formik.setValues({
                title: data.title,
                content: data.content,
                author: data.author,
                blog_images: Array.isArray(data.blog_images)
                    ? data.blog_images.map((img) => (typeof img === "string" ? img : img.url))
                    : [],
            });
        }
    };

    const handleCreateClose = () => {
        setCreateopen(false);
        setBlogData("");
        formik.resetForm();
    };

    const handleview = (blog) => {
        setSelectedBlog(blog);
        setViewOpen(true);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
        >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Blog Posts</h1>
                    <p className="text-gray-400">Manage all blog posts.</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCreateopen(true)}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all"
                >
                    <FaPlus /> Add New Post
                </motion.button>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
                <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full sm:w-80 group">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Search articles..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white focus:border-primary/50 outline-none transition-all"
                        />
                    </div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                        Total: {filteredData.length} Articles
                    </p>
                </div>

                <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-white/[0.02]">
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Article</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Preview</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Author</th>
                                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {currentItems.map((item, index) => (
                                <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            {item.blog_images?.[0] && (
                                                <img
                                                    src={typeof item.blog_images[0] === 'string' ? item.blog_images[0] : item.blog_images[0].url}
                                                    alt=""
                                                    className="w-12 h-12 rounded-xl object-cover border border-white/10"
                                                />
                                            )}
                                            <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">{item.title}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-xs text-gray-400 line-clamp-2 max-w-md">{item.content}</p>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="px-3 py-1 whitespace-nowrap rounded-full bg-white/5 border border-white/10 text-xs text-gray-300 font-medium">{item.author}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleview(item)}
                                                className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all border border-white/5"
                                            >
                                                <FaEye size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleOpen(item)}
                                                className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500 hover:text-white transition-all border border-green-500/20"
                                            >
                                                <RiEdit2Fill size={14} />
                                            </button>
                                            <button
                                                onClick={() => { setBlogData(item); setDelOpen(true); }}
                                                className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all border border-red-500/20"
                                            >
                                                <RiDeleteBin6Fill size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {currentItems.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="px-8 py-12 text-center text-gray-500 italic">No articles found matching your search.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="p-6 border-t border-white/5 flex justify-end">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, page) => setCurrentPage(page)}
                            sx={{
                                "& .MuiPaginationItem-root": { color: "#9ca3af", borderColor: "rgba(255,255,255,0.1)", borderRadius: "12px" },
                                "& .Mui-selected": { backgroundColor: "rgba(98,99,237,0.2) !important", color: "#6263ED !important", borderColor: "rgba(98,99,237,0.5) !important", fontWeight: "bold" }
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Modal: Add/Edit Blog */}
            <Modal open={createopen} onClose={handleCreateClose} className="flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-[#141414] border border-white/10 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto outline-none shadow-2xl no-scrollbar"
                >
                    <div className="p-8 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#141414] z-10">
                        <h2 className="text-2xl font-bold text-white">{blogData ? "Edit Article" : "Create Article"}</h2>
                        <button onClick={handleCreateClose} className="p-2 rounded-xl bg-white/5 text-gray-400 hover:text-white transition-colors">
                            <FaTimes size={20} />
                        </button>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Article Title</label>
                            <input
                                name="title"
                                type="text"
                                placeholder="The future of gaming..."
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                                onChange={formik.handleChange}
                                value={formik.values.title}
                            />
                            {formik.touched.title && formik.errors.title && <p className="text-red-400 text-xs ml-1">{formik.errors.title}</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Author Name</label>
                            <input
                                name="author"
                                type="text"
                                placeholder="John Doe"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all"
                                onChange={formik.handleChange}
                                value={formik.values.author}
                            />
                            {formik.touched.author && formik.errors.author && <p className="text-red-400 text-xs ml-1">{formik.errors.author}</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Content</label>
                            <textarea
                                name="content"
                                rows="6"
                                placeholder="Start writing..."
                                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all resize-none"
                                onChange={formik.handleChange}
                                value={formik.values.content}
                            />
                            {formik.touched.content && formik.errors.content && <p className="text-red-400 text-xs ml-1">{formik.errors.content}</p>}
                        </div>

                        <div className="space-y-4">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Images</label>
                            <div className="flex flex-wrap gap-4">
                                {formik.values.blog_images.map((image, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                            alt=""
                                            className="w-24 h-24 rounded-2xl object-cover border border-white/10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => formik.setFieldValue("blog_images", formik.values.blog_images.filter((_, i) => i !== index))}
                                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg"
                                        >
                                            <FaTimes size={10} />
                                        </button>
                                    </div>
                                ))}
                                <label className="w-24 h-24 rounded-2xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 hover:border-primary/50 transition-all group">
                                    <FaPlus className="text-gray-500 group-hover:text-primary transition-colors" />
                                    <span className="text-[10px] font-bold text-gray-500 mt-2 uppercase">Add</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                            const files = Array.from(e.target.files);
                                            formik.setFieldValue("blog_images", [...formik.values.blog_images, ...files]);
                                        }}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="pt-4 flex gap-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 py-4 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all disabled:opacity-50"
                            >
                                {loading ? "Processing..." : blogData ? "Update Article" : "Create Article"}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </Modal>

            {/* Modal: View Blog */}
            <Modal open={viewOpen} onClose={() => setViewOpen(false)} className="flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#141414] border border-white/10 rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto outline-none shadow-2xl p-8 no-scrollbar"
                >
                    {selectedBlog && (
                        <div className="space-y-8">
                            <div className="flex justify-between items-start">
                                <div className="space-y-2">
                                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-widest border border-primary/20">Featured Article</span>
                                    <h2 className="text-3xl font-bold text-white tracking-tight leading-tight">{selectedBlog.title}</h2>
                                    <p className="text-gray-400 font-medium flex items-center gap-2">By <span className="text-white">{selectedBlog.author}</span></p>
                                </div>
                                <button onClick={() => setViewOpen(false)} className="p-2 rounded-xl bg-white/5 text-gray-400 hover:text-white transition-colors">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            {selectedBlog.blog_images?.[0] && (
                                <img
                                    src={typeof selectedBlog.blog_images[0] === 'string' ? selectedBlog.blog_images[0] : selectedBlog.blog_images[0].url}
                                    alt=""
                                    className="w-full h-80 object-cover rounded-3xl border border-white/10"
                                />
                            )}

                            <div className="prose prose-invert max-w-none">
                                <p className="text-gray-300 leading-relaxed text-base whitespace-pre-wrap">{selectedBlog.content}</p>
                            </div>

                            {selectedBlog.blog_images?.length > 1 && (
                                <div className="grid grid-cols-3 gap-4">
                                    {selectedBlog.blog_images.slice(1).map((img, i) => (
                                        <img key={i} src={typeof img === 'string' ? img : img.url} className="w-full aspect-video object-cover rounded-2xl border border-white/10" />
                                    ))}
                                </div>
                            )}

                            <div className="pt-8 border-t border-white/5 flex justify-end">
                                <button onClick={() => setViewOpen(false)} className="px-8 py-3 rounded-2xl bg-white/5 text-white font-bold border border-white/10 hover:bg-white/10 transition-all">Close</button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </Modal>

            {/* Modal: Delete Confirmation */}
            <Modal open={delOpen} onClose={() => setDelOpen(false)} className="flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-[#141414] border border-white/10 rounded-3xl p-8 text-center w-full max-w-[360px] shadow-2xl"
                >
                    <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-6 text-2xl border border-red-500/20">
                        <RiDeleteBin6Fill />
                    </div>
                    <h2 className="text-xl font-bold mb-2 text-white">Delete Article?</h2>
                    <p className="text-gray-400 text-sm mb-8">Are you sure you want to delete <span className="text-white font-bold">"{blogData?.title}"</span>? This action is permanent.</p>
                    <div className="flex gap-4">
                        <button onClick={() => setDelOpen(false)} className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-white font-bold">Cancel</button>
                        <button
                            onClick={() => { dispatch(deleteBlog({ _id: blogData._id })); setDelOpen(false); }}
                            className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold shadow-[0_0_20px_rgba(239,68,68,0.3)]"
                        >
                            Delete
                        </button>
                    </div>
                </motion.div>
            </Modal>
        </motion.div>
    )
}
